import React, { useCallback } from 'react';
import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';
import { FaBookOpen, FaClock, FaCode, FaPlay, FaRocket, FaVideo } from 'react-icons/fa';
import useUser from '../../hooks/useUser';
import useCheckoutRedirect from '../../hooks/useCheckoutRedirect';
import { pushEvent, ANALYTICS_EVENTS } from '../../utils/analytics';
import './AIBootcampSelfPacedPage.css';

const imageBase = '/images/revenue';

const AIBootcampSelfPacedPage = () => {
  const { user } = useUser();
  const { startCheckout, processing, checkoutError } = useCheckoutRedirect('ai_bootcamp_self_paced');

  const startSelfPacedCheckout = useCallback((ctaLocation = 'self_paced_primary_cta') => {
    pushEvent({
      event: ANALYTICS_EVENTS.BOOTCAMP_PATH_SELECTED,
      source: window.location.pathname,
      path: 'self_paced',
      cta_location: ctaLocation,
      productSlug: 'ai-bootcamp-self-paced',
      planKey: 'self-paced-launch',
      destination: '/checkout',
      userId: user?.id || user?.sub || user?.userId,
      email: user?.email,
    });

    pushEvent({
      event: ANALYTICS_EVENTS.BOOTCAMP_CHECKOUT_STARTED,
      source: window.location.pathname,
      path: 'self_paced',
      cta_location: ctaLocation,
      productSlug: 'ai-bootcamp-self-paced',
      planKey: 'self-paced-launch',
      userId: user?.id || user?.sub || user?.userId,
      email: user?.email,
    });

    return startCheckout({
      products: [{ productSlug: 'ai-bootcamp-self-paced', planKey: 'self-paced-launch' }],
      flowContext: 'ai_bootcamp_self_paced',
      metadata: {
        origin: {
          flow: 'ai_bootcamp_self_paced',
          ctaLocation,
          returnUrl: '/ai-bot-builder-bootcamp/watch',
          priceAnchor: 997,
          launchPrice: 499,
        },
      },
      contact: {
        email: user?.email || '',
        name: user?.name || '',
      },
    });
  }, [startCheckout, user?.email, user?.id, user?.name, user?.sub, user?.userId]);

  return (
    <main className="self-paced-bootcamp-page">
      <Helmet>
        <title>Self-Paced AI Bootcamp | BotSpot</title>
        <meta
          name="description"
          content="Learn the AI Bootcamp on your schedule. Build an AI investing workflow inside BotSpot with recorded lessons, materials, 30 days of Pro, and a live upgrade path."
        />
      </Helmet>

      <section className="spb-hero">
        <div className="spb-hero-copy">
          <p className="spb-kicker">Self-Paced AI Bootcamp</p>
          <h1>Learn how to build AI investing workflows inside BotSpot.</h1>
          <p className="spb-lede">
            This is the lower-cost version of our AI Bootcamp for people who want the training now without joining the live Wednesday cohort.
            You learn the same core path: define an investing idea, turn it into strategy logic, test it, improve it, and understand what to build next.
          </p>
          <div className="spb-price-row">
            <span className="spb-price">$499</span>
            <span className="spb-anchor">Normally $997</span>
            <span className="spb-price-note">one-time launch price</span>
          </div>
          <div className="spb-cta-row">
            <button
              type="button"
              className="spb-primary-btn"
              onClick={() => startSelfPacedCheckout('hero')}
              disabled={processing}
            >
              <FaRocket /> {processing ? 'Opening checkout...' : 'Start the course'}
            </button>
            <Link className="spb-secondary-link" to="/ai-bot-builder-bootcamp">
              Want the live cohort?
            </Link>
          </div>
          {checkoutError && <p className="spb-error">{checkoutError}</p>}
        </div>
        <div className="spb-hero-media">
          <img
            src={`${imageBase}/self-paced-bootcamp-hero.webp`}
            alt="Self-paced AI Bootcamp replay workspace with charts, workflow cards, and code"
            width="1024"
            height="572"
            loading="eager"
          />
        </div>
      </section>

      <section className="spb-proof-band">
        <div><FaVideo /><span>Recorded Bootcamp lessons</span></div>
        <div><FaBookOpen /><span>Resources and summaries</span></div>
        <div><FaClock /><span>Learn on your schedule</span></div>
        <div><FaCode /><span>Build inside BotSpot</span></div>
      </section>

      <section className="spb-intro">
        <p className="spb-kicker">What This Course Is</p>
        <h2>AI Bootcamp teaches the workflow behind building with BotSpot.</h2>
        <p>
          Most people do not need another generic AI course. They need a practical path for turning an investing idea into something they can test and improve.
          The Bootcamp is built around that path. You work through the setup, strategy logic, backtesting, debugging, risk thinking, and launch preparation that go into building AI-assisted investing workflows.
        </p>
      </section>

      <section className="spb-split">
        <div className="spb-image-stack">
          <img
            src={`${imageBase}/self-paced-bootcamp-archive.webp`}
            alt="Bootcamp replay archive with lessons, chapters, and workflow modules"
            width="1024"
            height="572"
            loading="lazy"
          />
        </div>
        <div className="spb-section-copy">
          <p className="spb-kicker">What You Get</p>
          <h2>The Bootcamp course path, organized for implementation.</h2>
          <p>
            This version gives you the recorded lessons and supporting materials so you can start right away.
            It is not the live cohort, so it does not include live Q&A or live help during class, but it gives you the training path without waiting for the next live schedule.
          </p>
          <ul className="spb-check-list">
            <li>Bootcamp recordings from prior cohorts and supporting materials.</li>
            <li>Replay access through the Bootcamp watch portal.</li>
            <li>Course resources and summaries as they are available in the portal.</li>
            <li>30 days of BotSpot Pro so you can build while you learn.</li>
            <li>Apply what you paid toward a live cohort seat when you want direct help.</li>
          </ul>
        </div>
      </section>

      <section className="spb-curriculum">
        <p className="spb-kicker">Course Path</p>
        <h2>Five weeks of AI investing workflow training, built around action.</h2>
        <div className="spb-week-grid">
          {[
            ['Week 1', 'Set up the BotSpot workflow, understand the tools, and define the investing idea you want to test.'],
            ['Week 2', 'Turn the idea into rules, prompts, and first strategy logic that can be tested.'],
            ['Week 3', 'Backtest the workflow, find what breaks, and improve the strategy behavior.'],
            ['Week 4', 'Work through options, futures, risk controls, and the practical edge cases that matter before launch.'],
            ['Week 5', 'Review the workflow, refine the build, and plan what to improve or automate next.'],
          ].map(([label, text]) => (
            <div className="spb-week" key={label}>
              <strong>{label}</strong>
              <span>{text}</span>
            </div>
          ))}
        </div>
      </section>

      <section className="spb-compare">
        <div>
          <p className="spb-kicker">Choose the Right Version</p>
          <h2>Self-paced is for starting now. Live is for direct help.</h2>
        </div>
        <div className="spb-compare-grid">
          <article>
            <h3>Self-paced Bootcamp</h3>
            <p>$499 launch price. Recorded lessons, materials, 30 days of Pro, and upgrade credit toward a future live cohort.</p>
          </article>
          <article>
            <h3>Live Bootcamp</h3>
            <p>Use this when you want live sessions, Q&A, accountability, and direct implementation help in the next scheduled cohort.</p>
          </article>
        </div>
      </section>

      <section className="spb-final-cta">
        <div>
          <p className="spb-kicker">Launch Offer</p>
          <h2>Start the self-paced AI Bootcamp for $499.</h2>
          <p>This is a one-time launch price for the recorded course path. If you later want live help, your self-paced purchase can be credited toward a future live cohort seat.</p>
        </div>
        <button
          type="button"
          className="spb-primary-btn"
          onClick={() => startSelfPacedCheckout('final_cta')}
          disabled={processing}
        >
          <FaPlay /> {processing ? 'Opening checkout...' : 'Start course'}
        </button>
      </section>
    </main>
  );
};

export default AIBootcampSelfPacedPage;
